import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Share2, Calendar, User, Check, Twitter, Facebook, Linkedin, Link2 } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import Header from "../components/Header";
import SideMenu from "../components/SideMenu";
import SiteFooter from "../components/SiteFooter";
import { toast } from "sonner";

function formatLongDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

function readingTime(text) {
  if (!text) return "1 min read";
  const words = text.trim().split(/\s+/).length;
  return `${Math.max(1, Math.round(words / 220))} min read`;
}

export default function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const [art, set, all] = await Promise.all([
          api.get(`/news/${id}`),
          api.get("/settings"),
          api.get("/news"),
        ]);
        setArticle(art.data);
        setSettings(set.data);
        setRelated(all.data.filter(a => a.id !== art.data.id && a.category === art.data.category).slice(0, 3));
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
  }, [id]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article?.title,
          text: article?.description,
          url: shareUrl,
        });
      } catch {}
    } else {
      setShareOpen((v) => !v);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Header onMenuClick={() => setMenuOpen(true)} menuOpen={menuOpen} />
        <div data-testid="article-loading" className="font-serif-display text-3xl text-[#1A1A1A]">
          Loading story
          <span className="inline-block ml-2 w-2 h-2 rounded-full bg-[#D4A373] animate-pulse" />
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center px-6 text-center">
        <Header onMenuClick={() => setMenuOpen(true)} menuOpen={menuOpen} />
        <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} onSelectCategory={(c) => navigate(c ? `/?cat=${c}` : "/")} activeCategory={null} />
        <p className="overline text-[#76746F] mb-4">404</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl mb-6">Story not found.</h1>
        <Link to="/" data-testid="back-home" className="px-6 py-3 rounded-full border border-black/20 hover:bg-black hover:text-white hover:border-black transition-colors text-sm">
          Back to the deck
        </Link>
      </div>
    );
  }

  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(shareUrl)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]">
      <Header onMenuClick={() => setMenuOpen(true)} menuOpen={menuOpen} />
      <SideMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onSelectCategory={() => navigate("/")}
        activeCategory={null}
      />

      {/* Hero */}
      <section className="relative w-full h-[72vh] min-h-[520px] overflow-hidden">
        <img
          src={fileUrl(article.image_url)}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 qd-image-overlay" />
        <div className="absolute inset-x-0 bottom-0 px-6 sm:px-12 lg:px-20 pb-16 text-white">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-5">
              <span data-testid="article-category" className="overline px-3 py-1 border border-white/40 rounded-full">
                {article.category}
              </span>
              <span className="overline text-white/70">{formatLongDate(article.created_at)}</span>
              <span className="overline text-white/70 hidden sm:inline">· {readingTime(article.content || article.description)}</span>
            </div>
            <h1
              data-testid="article-title"
              className="font-serif-display text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight"
            >
              {article.title}
            </h1>
            <p className="mt-6 font-sans-modern text-lg text-white/85 leading-relaxed max-w-2xl">
              {article.description}
            </p>
          </div>
        </div>
      </section>

      {/* Toolbar */}
      <div className="border-b border-black/10 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 py-5 flex items-center justify-between gap-6">
          <Link
            to="/"
            data-testid="article-back"
            className="inline-flex items-center gap-2 text-sm text-[#76746F] hover:text-[#1A1A1A] transition-colors"
          >
            <ArrowLeft size={14} /> Back to the deck
          </Link>

          <div className="flex items-center gap-5 text-sm">
            <span className="flex items-center gap-1.5 text-[#76746F]">
              <User size={14} strokeWidth={1.5} />
              {article.editor_name}
            </span>
            <span className="hidden sm:flex items-center gap-1.5 text-[#76746F]">
              <Calendar size={14} strokeWidth={1.5} />
              {formatTime(article.created_at)}
            </span>

            <div className="relative">
              <button
                data-testid="share-button"
                type="button"
                onClick={handleNativeShare}
                className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-4 py-2 rounded-full hover:bg-[#D4A373] transition-colors text-sm"
              >
                <Share2 size={14} strokeWidth={1.75} />
                Share
              </button>
              {shareOpen && (
                <div
                  data-testid="share-panel"
                  className="absolute right-0 top-12 z-30 w-56 bg-white border border-black/10 rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-2"
                >
                  <ShareItem href={tweetUrl} icon={<Twitter size={14} />} label="Share on Twitter" testId="share-twitter" />
                  <ShareItem href={fbUrl} icon={<Facebook size={14} />} label="Share on Facebook" testId="share-facebook" />
                  <ShareItem href={liUrl} icon={<Linkedin size={14} />} label="Share on LinkedIn" testId="share-linkedin" />
                  <button
                    data-testid="share-copy"
                    onClick={copyLink}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-black/5"
                  >
                    {copied ? <Check size={14} /> : <Link2 size={14} />}
                    {copied ? "Copied" : "Copy link"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <article className="max-w-3xl mx-auto px-6 sm:px-12 py-16">
        <p className="font-serif-display text-2xl sm:text-3xl text-[#1A1A1A] leading-snug mb-10 italic">
          {article.description}
        </p>
        <div
          data-testid="article-body"
          className="font-sans-modern text-lg text-[#1A1A1A]/90 leading-[1.85] whitespace-pre-line"
        >
          {article.content || article.description}
        </div>

        <div className="mt-14 pt-8 border-t border-black/10 flex items-center justify-between">
          <div className="text-sm text-[#76746F]">
            Written by <span className="text-[#1A1A1A]">{article.editor_name}</span>
          </div>
          <button
            data-testid="share-bottom"
            type="button"
            onClick={handleNativeShare}
            className="inline-flex items-center gap-2 border border-black/15 px-4 py-2 rounded-full hover:bg-black hover:text-white hover:border-black transition-colors text-sm"
          >
            <Share2 size={14} strokeWidth={1.75} /> Share this story
          </button>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-black/10 bg-[#F3F1EC]">
          <div className="max-w-6xl mx-auto px-6 sm:px-12 py-16">
            <p className="overline text-[#76746F] mb-3">More in {article.category}</p>
            <h3 className="font-serif-display text-3xl sm:text-4xl tracking-tight mb-10">Keep reading.</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link
                  to={`/article/${r.id}`}
                  key={r.id}
                  data-testid={`related-${r.id}`}
                  className="group block"
                >
                  <div className="relative aspect-[4/5] overflow-hidden rounded-md mb-4">
                    <img src={fileUrl(r.image_url)} alt={r.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <p className="overline text-[#76746F] mb-2">{r.category} · {formatLongDate(r.created_at)}</p>
                  <h4 className="font-serif-display text-2xl leading-tight group-hover:underline underline-offset-4">{r.title}</h4>
                  <p className="text-sm text-[#76746F] mt-2 line-clamp-2">{r.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <SiteFooter settings={settings} />
    </div>
  );
}

function ShareItem({ href, icon, label, testId }) {
  return (
    <a
      data-testid={testId}
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-black/5"
    >
      {icon}
      {label}
    </a>
  );
}
